import React, { useState, useRef, useLayoutEffect, useEffect, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import styles from './DropdownMenu.module.css';

const DropdownMenuContext = createContext(null);

function setRef(ref, value) {
    if (ref == null) return;
    if (typeof ref === 'function') ref(value);
    else ref.current = value;
}

/**
 * Accessible dropdown menu that portals its list to document.body so it's never clipped.
 * Trigger must be a single React element (e.g. <button>); ref and onClick are merged onto it.
 */
const DropdownMenu = ({ trigger, children, align = 'right', 'aria-label': ariaLabel = 'Menu' }) => {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    const MENU_ESTIMATED_HEIGHT = 340;
    const PADDING = 8;
    const VIEWPORT_PAD = 16;

    useLayoutEffect(() => {
        if (!open) {
            setPosition(null);
            return;
        }
        const measure = () => {
            const el = triggerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const menuWidth = 200;
            const left = align === 'right'
                ? Math.min(rect.left, window.innerWidth - menuWidth - VIEWPORT_PAD)
                : rect.left;
            const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PAD;
            const openUpward = spaceBelow < MENU_ESTIMATED_HEIGHT;
            setPosition({
                left,
                zIndex: 100000,
                openUpward,
                ...(openUpward
                    ? { bottom: window.innerHeight - rect.top + PADDING }
                    : { top: rect.bottom + PADDING }),
            });
        };
        measure();
        const id = requestAnimationFrame(() => {
            if (triggerRef.current) measure();
        });
        return () => cancelAnimationFrame(id);
    }, [open, align]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                triggerRef.current?.contains(e.target) ||
                menuRef.current?.contains(e.target)
            ) return;
            setOpen(false);
            setPosition(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!open) return;
        const handleScroll = () => {
            setOpen(false);
            setPosition(null);
        };
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [open]);

    const close = () => {
        setOpen(false);
        setPosition(null);
    };

    const toggle = (e) => {
        if (e) e.stopPropagation();
        setOpen((v) => !v);
    };

    const triggerElement = React.Children.only(trigger);
    const triggerWithProps = React.cloneElement(triggerElement, {
        ref: (el) => {
            triggerRef.current = el;
            setRef(triggerElement.ref, el);
        },
        onClick: (e) => {
            triggerElement.props?.onClick?.(e);
            toggle(e);
        },
        'aria-expanded': open,
        'aria-haspopup': 'menu',
    });

    const menuContent = position ? (
        <DropdownMenuContext.Provider value={{ close }}>
            <div
                ref={menuRef}
                className={styles.menu}
                role="menu"
                aria-label={ariaLabel}
                style={{
                    position: 'fixed',
                    left: position.left,
                    zIndex: position.zIndex,
                    ...(position.openUpward
                        ? {
                            bottom: position.bottom,
                            maxHeight: window.innerHeight - position.bottom - VIEWPORT_PAD,
                            overflowY: 'auto',
                        }
                        : { top: position.top }),
                }}
                onClick={close}
            >
                {children}
            </div>
        </DropdownMenuContext.Provider>
    ) : null;

    return (
        <div className={styles.wrap}>
            {triggerWithProps}
            {open && typeof document !== 'undefined' && createPortal(menuContent, document.body)}
        </div>
    );
};

const DropdownMenuItem = ({ children, onClick, icon: Icon, danger, disabled, ...props }) => {
    const menuContext = useContext(DropdownMenuContext);
    return (
        <button
            type="button"
            className={danger ? styles.itemDanger : styles.item}
            onClick={(e) => {
                e.stopPropagation();
                if (disabled) return;
                menuContext?.close?.();
                onClick?.(e);
            }}
            disabled={disabled}
            role="menuitem"
            {...props}
        >
            {Icon && <Icon size={14} className={styles.itemIcon} />}
            {children}
        </button>
    );
};

const DropdownMenuDivider = () => <div className={styles.divider} role="separator" />;

DropdownMenu.Item = DropdownMenuItem;
DropdownMenu.Divider = DropdownMenuDivider;

export default DropdownMenu;
